import React, { useEffect, useRef, useState } from 'react';
import { Drawer, DrawerContent, DrawerDescription, DrawerTitle, DrawerClose } from './ui/drawer';
import { Button } from './ui/button';
import { X, ArrowUp, Loader2 } from 'lucide-react';
import type { LoginPromptActionType } from '@hooks/useLoginPrompt';
import { useAmplitudeTracking } from '@hooks/useAmplitudeTracking';
import { ANALYTICS_EVENTS } from '@lib/analytics-events';
import { COVERS_POST_SHEET } from '@lib/postSheet/coversPostSheet';
import type { AddEventJoinMode } from '@utils/addEventAccess';

/**
 * A prompt that names what the guest just did instead of an action's stock words (TECH-197 B7,
 * "Your answer is saved"): its picture, title, line and button label. No Close button and no
 * footer; dismissed by a swipe, the scrim or Escape.
 */
export interface LoginPromptContent {
  media: React.ReactNode;
  title: string;
  description: string;
  buttonLabel: string;
}

interface LoginPromptModalProps {
  isOpen: boolean;
  onClose: () => void;
  actionType?: LoginPromptActionType;
  onLogin?: () => void;
  content?: LoginPromptContent;
  /** Only read when `actionType` is `create_event`: names the Space the person is adding an event to. */
  space?: { name: string; mode: AddEventJoinMode };
}

/**
 * Icon types for action configs
 */
type ActionIcon = 'i_can_help' | 'me_too' | 'upvote' | 'meetball';

/**
 * Configuration for each action type's UI
 *
 * AIDEV-NOTE: Each action type should have a distinct visual treatment.
 * Actions are grouped by intent level:
 * - Question interactions: Custom icons (I Can Help, Me Too, Uplift)
 * - Event actions: Calendar/event icons
 * - Navigation actions: Standard icons with Meetball branding
 */
const ACTION_CONFIGS: Record<
  Exclude<LoginPromptActionType, 'generic'>,
  {
    icon: ActionIcon;
    title: string;
    description: string;
    footer?: string;
  }
> = {
  // Question interactions (highest intent) - custom icons with footer
  upvote: {
    icon: 'upvote',
    title: 'Sign in to uplift this post',
    description: 'Tapping "Uplift" says:\nThis post deserves attention!',
    footer: 'No likes. No public comments.\nJust people finding each other.',
  },
  me_too: {
    icon: 'me_too',
    title: 'Sign in to join',
    description:
      'Tapping "Me Too" says: I feel this too.\nPeople who relate can join a small group conversation.',
    footer: 'No likes. No public comments.\nJust real people who relate.',
  },
  i_can_help: {
    icon: 'i_can_help',
    title: 'Sign in to help',
    description:
      "This starts a private conversation.\nOnly the person you're helping will see your messages.",
    footer: 'No likes. No public comments.\nJust direct offers of help.',
  },

  // Event actions (high intent) - use meetball icon to match original generic mode
  event_checkin: {
    icon: 'meetball',
    title: 'Join to check in to this event',
    description: 'Check in to join the conversation.',
  },
  create_event: {
    icon: 'meetball',
    title: 'Sign in to add your event',
    description: "Then you'll land back here to add it.",
  },
  save_event: {
    icon: 'meetball',
    title: 'Join to save events',
    description: 'Sign in to keep the events you save.',
  },
  going_event: {
    icon: 'meetball',
    title: 'Join to RSVP',
    description: "Sign in to tell people you're going.",
  },
  view_my_events: {
    icon: 'meetball',
    title: 'Join to see your events',
    description: "Sign in to see the events you saved and where you're going.",
  },
  create_community: {
    icon: 'meetball',
    title: 'Join to create a community',
    description: 'Sign in to create your own Space and bring people together.',
  },
  join_community: {
    icon: 'meetball',
    title: 'Sign in to join this Space',
    description: 'Sign in to join Spaces and connect with communities on Meetball',
  },
  // A poll answered signed out: its drawer passes `content` (the pick); these words stand in only
  // for a caller that passes none.
  answer_poll: {
    icon: 'meetball',
    title: 'Sign in to answer',
    description: 'Sign in so your answer counts.',
  },
  // Content access (high intent)
  view_private_question: {
    icon: 'meetball',
    title: 'Join to see this post',
    description: "Sign in to see what they're looking for and how you can help.",
  },

  // Navigation actions (medium intent) - use meetball icon to match original generic mode
  access_profile: {
    icon: 'meetball',
    title: 'Join to access your profile',
    description: 'Sign in to show people who you are.',
  },
  connect: {
    icon: 'meetball',
    title: 'Join to connect with others',
    description: 'Sign in to reach the people you want to meet.',
  },
  access_messages: {
    icon: 'meetball',
    title: 'Join to access messages',
    description: 'Sign in to keep your conversations going.',
  },
};

/**
 * Modal that prompts guest users to sign in for protected actions
 *
 * Uses typed actionType to determine custom icon, title, description, and footer.
 * Each action type has a specific visual treatment defined in ACTION_CONFIGS.
 */
export const LoginPromptModal: React.FC<LoginPromptModalProps> = ({
  isOpen,
  onClose,
  actionType = 'generic',
  onLogin,
  content,
  space,
}) => {
  const { trackEvent } = useAmplitudeTracking();
  const hasTrackedShow = useRef(false);
  const [isSigningIn, setIsSigningIn] = useState(false);
  const [lastOpen, setLastOpen] = useState(isOpen);

  // Reset loading state when modal closes
  if (lastOpen !== isOpen) {
    setLastOpen(isOpen);
    if (!isOpen) {
      setIsSigningIn(false);
    }
  }

  useEffect(() => {
    if (!isOpen) {
      hasTrackedShow.current = false;
    }
  }, [isOpen]);

  // Track when modal is shown (only once per open)
  useEffect(() => {
    if (isOpen && !hasTrackedShow.current) {
      hasTrackedShow.current = true;
      trackEvent(ANALYTICS_EVENTS.login_prompt_shown, {
        action_type: actionType,
      });
    }
  }, [isOpen, actionType, trackEvent]);

  const handleLogin = () => {
    if (isSigningIn) return;
    setIsSigningIn(true);
    trackEvent(ANALYTICS_EVENTS.login_prompt_sign_in_clicked, {
      action_type: actionType,
    });
    if (onLogin) {
      onLogin();
    }
  };

  const handleDismiss = () => {
    trackEvent(ANALYTICS_EVENTS.login_prompt_dismissed, {
      action_type: actionType,
    });
    onClose();
  };

  // Determine if we have a specific config for this action type
  const isActionSpecific = actionType !== 'generic' && actionType in ACTION_CONFIGS;
  const config = isActionSpecific ? ACTION_CONFIGS[actionType] : null;

  // Render the appropriate icon
  const renderIcon = () => {
    if (!config) {
      // Generic mode: show Meetball logo
      return (
        <div className="w-24 h-24 rounded-full bg-mb-yellow flex items-center justify-center mb-6 overflow-hidden">
          <img src="/icons/logo-icon.png" alt="Meetball" className="w-16 h-16 object-contain" />
        </div>
      );
    }

    // Action-specific icons
    switch (config.icon) {
      case 'upvote':
        return (
          <div className="w-20 h-20 rounded-full bg-mb-teal/20 flex items-center justify-center mb-6">
            <ArrowUp className="w-10 h-10 text-mb-teal" />
          </div>
        );
      case 'me_too':
        return (
          <div className="w-20 h-20 flex items-center justify-center mb-6">
            <img src="/Metoo (1).svg" alt="" className="w-16 h-16" />
          </div>
        );
      case 'i_can_help':
        return (
          <div className="w-20 h-20 flex items-center justify-center mb-6">
            <img src="/I Can Help.svg" alt="" className="w-16 h-16" />
          </div>
        );
      case 'meetball':
      default:
        return (
          <div className="w-24 h-24 rounded-full bg-mb-yellow flex items-center justify-center mb-6 overflow-hidden">
            <img src="/icons/logo-icon.png" alt="Meetball" className="w-16 h-16 object-contain" />
          </div>
        );
    }
  };

  // Get title and description from config or use generic defaults. A create_event prompt that
  // comes from a Space calendar names the Space and says whether the person joins or asks to.
  const spaceCopy =
    actionType === 'create_event' && space
      ? {
          title: `Sign in to add your event to ${space.name}`,
          description:
            space.mode === 'open'
              ? `You'll join ${space.name} on the way.`
              : `You'll ask to join ${space.name} on the way.`,
        }
      : null;
  const displayTitle = spaceCopy?.title ?? config?.title ?? 'Join Meetball';
  const displayDescription =
    spaceCopy?.description ?? config?.description ?? 'Sign in to connect with people on Meetball.';

  if (content) {
    return (
      <Drawer
        open={isOpen}
        onOpenChange={open => {
          if (!open) handleDismiss();
        }}
      >
        <DrawerContent
          className="flex flex-col items-center gap-3 border-0 border-t border-slate-200 bg-white px-4 pb-safe-offset-8"
          data-testid="login-prompt-content"
          {...COVERS_POST_SHEET}
        >
          <div className="mt-2 flex flex-col items-center gap-3">
            {content.media}
            <DrawerTitle className="text-center text-lg leading-[22px] font-semibold text-mb-ink">
              {content.title}
            </DrawerTitle>
            <DrawerDescription className="w-[300px] max-w-full text-center text-sm leading-[21px] text-mb-dark-gray">
              {content.description}
            </DrawerDescription>
          </div>
          <Button
            onClick={handleLogin}
            disabled={isSigningIn}
            className="mt-1 h-12 w-full rounded-full bg-mb-teal-ink px-8 text-base leading-5 font-semibold text-white hover:bg-mb-teal-ink disabled:opacity-100"
          >
            {isSigningIn ? (
              <span className="flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>One sec</span>
              </span>
            ) : (
              content.buttonLabel
            )}
          </Button>
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Drawer
      open={isOpen}
      onOpenChange={open => {
        if (!open) handleDismiss();
      }}
    >
      <DrawerContent className="flex flex-col bg-white">
        {/* Close button */}
        <DrawerClose asChild>
          <button
            type="button"
            className="absolute top-1.5 right-1.5 z-10 flex h-11 w-11 items-center justify-center rounded-full text-gray-400 hover:text-gray-600"
            aria-label="Close"
          >
            <X className="w-6 h-6" />
          </button>
        </DrawerClose>

        {/* Accessibility labels */}
        <DrawerTitle className="sr-only">Sign In Required</DrawerTitle>
        <DrawerDescription className="sr-only">
          {displayTitle}. {displayDescription}
        </DrawerDescription>

        {/* Content */}
        <div className="flex flex-col items-center px-6 pt-8 pb-4">
          {/* Icon */}
          {renderIcon()}

          {/* Title */}
          <h3 className="text-xl font-bold text-gray-900 mb-3 text-center break-words">
            {displayTitle}
          </h3>

          {/* Description */}
          <p className="text-sm text-gray-600 text-center max-w-xs leading-relaxed whitespace-pre-line break-words">
            {displayDescription}
          </p>
        </div>

        {/* Button */}
        <div className="flex flex-col gap-3 px-6 pb-4">
          <Button
            onClick={handleLogin}
            disabled={isSigningIn}
            className="bg-mb-yellow hover:bg-mb-yellow text-black font-semibold rounded-full px-8 w-full h-12 shadow-glow-yellow transition-all duration-200 disabled:opacity-100"
          >
            {isSigningIn ? (
              <span className="flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>One sec</span>
              </span>
            ) : (
              'Sign In'
            )}
          </Button>
        </div>

        {/* Footer text */}
        {config?.footer && (
          <div className="px-6 pb-8">
            <p className="text-xs text-gray-400 text-center whitespace-pre-line leading-relaxed">
              {config.footer}
            </p>
          </div>
        )}
      </DrawerContent>
    </Drawer>
  );
};
